import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router';
import { useAuth } from '@/hooks/use-auth';
import { Shield, ExternalLink, AlertTriangle, CheckCircle } from 'lucide-react';
import { Logo } from '@/components/ui/logo';
import { Spinner } from '@/components/ui/spinner';
import api from '@/lib/api';

const SCOPE_LABELS: Record<string, { label: string; description: string }> = {
  'strategies:read': { label: 'Read Strategies', description: 'View your strategies, rules, and versions' },
  'strategies:write': { label: 'Write Strategies', description: 'Create, edit, and publish strategies' },
  'backtests:run': { label: 'Run Backtests', description: 'Execute backtests on strategies' },
  'bots:read': { label: 'Read Bots', description: 'View bots, trades, and performance' },
  'bots:write': { label: 'Write Bots', description: 'Create, start, stop, and manage bots' },
  'accounts:read': { label: 'Read Accounts', description: 'View MT5 accounts and portfolio' },
  'analysis:run': { label: 'Run Analysis', description: 'Analyze trade history and generate insights' },
};

interface AuthInfo {
  client_name: string;
  client_logo_url: string | null;
  website_url: string | null;
  requested_scopes: string[];
  redirect_uri: string;
  code_challenge?: string;
  code_challenge_method?: string;
  state?: string;
}

export default function OAuthAuthorizePage() {
  const { token, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [authInfo, setAuthInfo] = useState<AuthInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [approving, setApproving] = useState(false);
  const [approved, setApproved] = useState(false);

  const clientId = searchParams.get('client_id') || '';
  const redirectUri = searchParams.get('redirect_uri') || '';
  const scope = searchParams.get('scope') || '';
  const state = searchParams.get('state') || '';
  const codeChallenge = searchParams.get('code_challenge') || '';
  const codeChallengeMethod = searchParams.get('code_challenge_method') || '';

  // Redirect to login if not authenticated, preserving the full OAuth URL
  useEffect(() => {
    if (authLoading) return;
    if (!token) {
      const returnUrl = `/oauth/authorize?${searchParams.toString()}`;
      navigate(`/login?redirect=${encodeURIComponent(returnUrl)}`, { replace: true });
    }
  }, [token, authLoading, navigate, searchParams]);

  // Fetch client info
  useEffect(() => {
    if (!token || !clientId) return;

    async function fetchAuthInfo() {
      try {
        const params = new URLSearchParams({
          client_id: clientId,
          redirect_uri: redirectUri,
          scope,
        });
        if (codeChallenge) params.set('code_challenge', codeChallenge);
        if (codeChallengeMethod) params.set('code_challenge_method', codeChallengeMethod);
        if (state) params.set('state', state);

        const res = await api.get(`/oauth/authorize?${params.toString()}`);
        setAuthInfo(res.data);
      } catch (err: any) {
        const msg = err?.response?.data?.message || 'Invalid authorization request';
        setError(msg);
      } finally {
        setLoading(false);
      }
    }

    fetchAuthInfo();
  }, [token, clientId, redirectUri, scope, codeChallenge, codeChallengeMethod, state]);

  async function handleApprove() {
    if (!authInfo) return;
    setApproving(true);
    setError('');

    try {
      const res = await api.post('/oauth/authorize', {
        client_id: clientId,
        redirect_uri: redirectUri,
        scope,
        state: state || undefined,
        code_challenge: codeChallenge || undefined,
        code_challenge_method: codeChallengeMethod || undefined,
      });

      // Show success state, then redirect after a brief delay
      setApproved(true);
      setTimeout(() => {
        window.location.href = res.data.redirect_url;
      }, 2000);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Authorization failed');
      setApproving(false);
    }
  }

  function handleDeny() {
    if (redirectUri) {
      const params = new URLSearchParams({ error: 'access_denied' });
      if (state) params.set('state', state);
      window.location.href = `${redirectUri}?${params.toString()}`;
    } else {
      navigate('/dashboard', { replace: true });
    }
  }

  if (authLoading || (!token && !error)) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <div className="w-full max-w-sm">
        {/* Logo */}
        <div className="flex justify-center mb-8">
          <Logo size="lg" />
        </div>

        {/* Card */}
        <div
          className="rounded-xl p-6"
          style={{
            background: 'var(--color-surface)',
            border: '1px solid var(--color-border)',
          }}
        >
          {approved ? (
            <div className="flex flex-col items-center py-10 gap-4">
              <div
                className="w-14 h-14 rounded-full flex items-center justify-center"
                style={{
                  background: 'linear-gradient(135deg, rgba(34,197,94,0.15), rgba(34,197,94,0.05))',
                  border: '1px solid rgba(34,197,94,0.25)',
                }}
              >
                <CheckCircle size={28} className="text-green" />
              </div>
              <div className="text-center">
                <h2 className="text-base font-extrabold mb-1">Connected!</h2>
                <p className="text-xs text-ink-3">
                  {authInfo?.client_name} now has access to your ForgeAlpha account.
                </p>
              </div>
              <div
                className="h-px w-full"
                style={{ background: 'linear-gradient(to right, transparent, rgba(34,197,94,0.2), transparent)' }}
              />
              <div className="flex flex-col items-center gap-2">
                <p className="text-[11px] text-ink-3">
                  Redirecting you back...
                </p>
                <Spinner size="sm" />
                <p className="text-[10px] text-ink-4 mt-1">
                  You can also close this tab — the connection is complete.
                </p>
              </div>
              <p className="text-[10px] text-ink-4 text-center mt-2">
                You can revoke access anytime in Settings &rarr; Connected Apps
              </p>
            </div>
          ) : loading ? (
            <div className="flex flex-col items-center py-8 gap-3">
              <Spinner />
              <p className="text-xs text-ink-3">Loading authorization request...</p>
            </div>
          ) : error && !authInfo ? (
            <div className="flex flex-col items-center py-8 gap-3">
              <AlertTriangle size={32} className="text-red" />
              <p className="text-sm font-semibold text-red text-center">{error}</p>
              <button
                onClick={() => navigate('/dashboard', { replace: true })}
                className="text-xs text-orange font-semibold hover:underline mt-2"
              >
                Go to Dashboard
              </button>
            </div>
          ) : authInfo ? (
            <>
              {/* Header */}
              <div className="text-center mb-5">
                <div className="flex items-center justify-center gap-3 mb-3">
                  {authInfo.client_logo_url ? (
                    <img
                      src={authInfo.client_logo_url}
                      alt={authInfo.client_name}
                      className="w-10 h-10 rounded-lg"
                    />
                  ) : (
                    <div
                      className="w-10 h-10 rounded-lg flex items-center justify-center"
                      style={{
                        background: 'linear-gradient(135deg, rgba(249,115,22,0.15), rgba(245,158,11,0.08))',
                        border: '1px solid rgba(249,115,22,0.2)',
                      }}
                    >
                      <Shield size={20} className="text-orange" />
                    </div>
                  )}
                </div>
                <h2 className="text-base font-extrabold">
                  {authInfo.client_name}
                </h2>
                <p className="text-xs text-ink-3 mt-1">
                  wants to access your ForgeAlpha account
                </p>
                {authInfo.website_url && (
                  <a
                    href={authInfo.website_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-[10px] text-ink-4 hover:text-ink-3 mt-1"
                  >
                    {new URL(authInfo.website_url).hostname}
                    <ExternalLink size={10} />
                  </a>
                )}
              </div>

              {/* Divider */}
              <div
                className="h-px mb-4"
                style={{ background: 'linear-gradient(to right, transparent, rgba(249,115,22,0.2), transparent)' }}
              />

              {/* Permissions */}
              <div className="mb-5">
                <p className="text-[11px] font-semibold text-ink-3 mb-2.5">
                  This will allow {authInfo.client_name} to:
                </p>
                <div className="space-y-2">
                  {authInfo.requested_scopes.map((scope) => {
                    const info = SCOPE_LABELS[scope];
                    return (
                      <div
                        key={scope}
                        className="flex items-start gap-2.5 px-3 py-2 rounded-lg"
                        style={{
                          background: 'var(--color-glass)',
                          border: '1px solid var(--color-glass-border)',
                        }}
                      >
                        <div className="w-1.5 h-1.5 rounded-full bg-orange mt-1.5 shrink-0" />
                        <div>
                          <p className="text-xs font-semibold">{info?.label || scope}</p>
                          {info?.description && (
                            <p className="text-[10px] text-ink-4 mt-0.5">{info.description}</p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {error && (
                <p className="text-xs text-red font-semibold mb-3">{error}</p>
              )}

              {/* Actions */}
              <div className="flex gap-3">
                <button
                  onClick={handleDeny}
                  disabled={approving}
                  className="flex-1 py-2.5 rounded-lg text-xs font-bold text-ink-3 transition-all hover:bg-glass-2 hover:text-ink disabled:opacity-50"
                  style={{
                    background: 'transparent',
                    border: '1px solid var(--color-glass-border)',
                  }}
                >
                  Deny
                </button>
                <button
                  onClick={handleApprove}
                  disabled={approving}
                  className="flex-1 py-2.5 rounded-lg text-xs font-bold text-white transition-all hover:brightness-110 disabled:opacity-50 flex items-center justify-center gap-2"
                  style={{
                    background: 'linear-gradient(135deg, #f97316, #f59e0b)',
                    boxShadow: '0 0 20px rgba(249,115,22,0.25)',
                  }}
                >
                  {approving ? <Spinner size="sm" /> : 'Authorize'}
                </button>
              </div>

              {/* Trust note */}
              <p className="text-[10px] text-ink-4 text-center mt-4">
                You can revoke access anytime in Settings
              </p>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}
